import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import AuthContext from './AuthContext';
import socket, { addJoinEvent, removeJoinEvent } from '../socket';

/**
 * EditorUIContext — everything about the editor that is not the graph.
 *
 * Selection, tabs, modals, drag state, and collaborator awareness live here so
 * that CanvasContext can be purely about the draft document. Splitting them is
 * what stops a keystroke in the sidebar from re-rendering the whole canvas: the
 * two contexts change on completely different cadences.
 *
 * Selection is stored as `{ type, id }` — never a copy of the selected entity.
 * The old editor kept `selectedSituation`/`selectedChoice`/`selectedGroup` as
 * duplicated objects that had to be re-synced on every edit; sidebars now look
 * the current data up from the graph by id, so there is nothing to fall out of
 * date.
 */

const EditorUIContext = createContext();

/** The four kinds of thing a collaborator can be "in". */
const SELECTION_TYPES = ['situation', 'choice', 'group', 'key'];

/**
 * How often to tell the server this tab is still here. Comfortably under
 * collabStore.PRESENCE_TTL_SECONDS (90s) so one missed beat on a slow network
 * does not make an editor flicker out of the room.
 */
const HEARTBEAT_MS = 25000;

const emptyRegistries = () => ({
    situation: {},
    choice: {},
    group: {},
    key: {},
});

const emptyQueues = () => ({
    situation: {},
    choice: {},
    group: {},
    key: {},
});

/**
 * Awareness bookkeeping, as two views of the same fact:
 *   registries[type][userId] = itemId   — what each user has selected
 *   queues[type][itemId] = [userId]     — who is on each item, in arrival order
 * The queue order is what decides whose edit-lock badge shows first.
 */
function moveUser(state, type, userId, nextId) {
    const registries = { ...state.registries, [type]: { ...state.registries[type] } };
    const queues = { ...state.queues, [type]: { ...state.queues[type] } };

    const previousId = registries[type][userId];
    if (previousId) {
        const remaining = (queues[type][previousId] || []).filter((id) => id !== userId);
        if (remaining.length) queues[type][previousId] = remaining;
        else delete queues[type][previousId];
    }

    if (nextId) {
        registries[type][userId] = nextId;
        const list = queues[type][nextId] || [];
        if (!list.includes(userId)) queues[type][nextId] = [...list, userId];
    } else {
        delete registries[type][userId];
    }

    return { registries, queues };
}

/** Clears a user out of every type — used when they leave the room. */
function removeUserEverywhere(state, userId) {
    let next = state;
    for (const type of SELECTION_TYPES) {
        if (next.registries[type][userId]) next = moveUser(next, type, userId, null);
    }
    return next;
}

/** Clears an item out entirely — used when it is deleted. */
function removeItemEverywhere(state, type, itemId) {
    const registries = { ...state.registries, [type]: { ...state.registries[type] } };
    const queues = { ...state.queues, [type]: { ...state.queues[type] } };

    delete queues[type][itemId];
    for (const [userId, value] of Object.entries(registries[type])) {
        if (value === itemId) delete registries[type][userId];
    }

    return { registries, queues };
}

export const EditorUIProvider = ({ scenarioId, children }) => {
    const { userId: authUserId, user } = useContext(AuthContext);

    // --- Local selection ----------------------------------------------------
    const [selection, setSelection] = useState({ type: null, id: null });
    const [selectedNodeIds, setSelectedNodeIds] = useState([]);
    const [edgeConnectingNodeId, setEdgeConnectingNodeId] = useState(null);

    // --- Panels and modals --------------------------------------------------
    const [activeEditTab, setActiveEditTab] = useState('1');
    const [showTocEditor, setShowTocEditor] = useState(false);
    const [isDragging, setIsDragging] = useState(false);

    // Which situation's text editor is open full-screen. Held here rather than in
    // EditSituation so that coming back from a preview can reopen it: the
    // editor remounts on that navigation and only the layout knows to restore it.
    const [expandedEditorNodeId, setExpandedEditorNodeId] = useState(null);

    // The key whose situations and choices the canvas is spotlighting. Local on
    // purpose: `selectedKeyId` means "I am editing this key" and is broadcast
    // to collaborators, whereas inspecting where a key is used is nobody's
    // business but the person looking.
    const [highlightedKeyId, setHighlightedKeyId] = useState(null);

    // Canvas.js owns the DOM node; the "create at centre" gesture needs its
    // bounds to know where the middle of the visible canvas actually is.
    const reactFlowWrapperRef = useRef(null);

    /** Starts a palette drag. The drop is handled by Canvas.js. */
    const onDragStart = useCallback((event, nodeType) => {
        event.dataTransfer.setData('application/reactflow', nodeType);
        event.dataTransfer.effectAllowed = 'move';
        setIsDragging(true);
    }, []);

    const onDragEnd = useCallback(() => setIsDragging(false), []);

    // --- Collaborator awareness --------------------------------------------
    const [awareness, setAwareness] = useState(() => ({
        registries: emptyRegistries(),
        queues: emptyQueues(),
    }));
    const [activeCollaboratorsList, setActiveCollaboratorsList] = useState([]);
    const [permissionLevel, setPermissionLevel] = useState('collaborator');

    /**
     * Who else is actually in the room right now.
     *
     * This used to be the scenario's `sharedUser` list being non-empty, which
     * answered a different question: it was true for a scenario shared with
     * someone who last opened it in March, and false for a room the site admin
     * had just walked into. Presence is the honest signal, and it is what decides
     * whether the awareness badges mean anything.
     */
    const otherCollaborators = useMemo(
        () => activeCollaboratorsList.filter((entry) => entry?.userId !== authUserId),
        [activeCollaboratorsList, authUserId]
    );
    const hasCollaborators = otherCollaborators.length > 0;

    // Socket handlers are registered once but need the latest awareness state to
    // answer a newcomer's sync request; a ref avoids re-subscribing on every
    // selection change.
    const awarenessRef = useRef(awareness);
    awarenessRef.current = awareness;

    const selectionRef = useRef(selection);
    selectionRef.current = selection;

    // Who the roster held last time, so a departure can be detected by diffing it
    // rather than by trusting an event that a killed browser never sends.
    const prevRosterIdsRef = useRef([]);
    // Whether our join has already been answered; see handleSync below.
    const hasSyncedRef = useRef(false);

    /**
     * Moves this user's selection and tells the room. `type` of null clears it.
     * @param {'situation'|'choice'|'group'|'key'|null} type
     * @param {string|null} id
     */
    const selectItem = useCallback(
        (type, id = null) => {
            const previous = selectionRef.current;
            const next = type && id ? { type, id } : { type: null, id: null };

            if (previous.type === next.type && previous.id === next.id) return;

            setSelection(next);
            setEdgeConnectingNodeId(null);
            // Picking one thing ends a marquee multi-select.
            setSelectedNodeIds(next.type === 'situation' && next.id ? [next.id] : []);

            if (!authUserId) return;

            setAwareness((state) => {
                let updated = state;
                if (previous.type) updated = moveUser(updated, previous.type, authUserId, null);
                if (next.type) updated = moveUser(updated, next.type, authUserId, next.id);
                return updated;
            });

            socket.emit('selectionChanged', {
                userId: authUserId,
                scenarioId,
                newSelection: { type: next.type, id: next.id },
                oldSelection: { type: previous.type, id: previous.id },
            });
        },
        [authUserId, scenarioId]
    );

    const clearSelection = useCallback(() => selectItem(null, null), [selectItem]);

    /** Multi-select from a marquee drag. Does not participate in awareness. */
    const selectNodes = useCallback((nodeIds) => {
        setSelectedNodeIds(nodeIds || []);
        setEdgeConnectingNodeId(null);
    }, []);

    /**
     * Called when an entity is deleted: drop it from everyone's awareness, and
     * clear the local selection if it was pointing at it.
     */
    const dropSelectionsOf = useCallback((type, itemId) => {
        setAwareness((state) => removeItemEverywhere(state, type, itemId));
        setSelection((current) => (current.id === itemId ? { type: null, id: null } : current));
        setSelectedNodeIds((current) => current.filter((id) => id !== itemId));
        if (type === 'key') {
            setHighlightedKeyId((current) => (current === itemId ? null : current));
        }
    }, []);

    // --- Socket: presence and awareness ------------------------------------
    //
    // Data changes are not relayed from here: the server rebroadcasts accepted op
    // batches into this same room and hooks/useCollabSync.js receives them. What
    // travels client-to-client is only who is looking at what.
    //
    // The room is joined unconditionally rather than only when the scenario has
    // collaborators configured. It has to be — an op batch can only reach another
    // editor if both are in the room, and "is anyone else here" is a question you
    // cannot answer without joining first.
    useEffect(() => {
        if (!authUserId || !scenarioId) return undefined;

        // A different scenario (or a different user) is a different room; nothing
        // this instance learned about the last one carries over.
        prevRosterIdsRef.current = [];
        hasSyncedRef.current = false;

        const joinEvent = 'joinSharedScenario';
        const join = () => {
            // Rejoining means we are asking the room to describe itself again, so
            // the answer we already accepted no longer counts.
            hasSyncedRef.current = false;
            return addJoinEvent(
                joinEvent,
                { userName: user?.username, userEmail: user?.email, scenarioId },
                undefined,
                // Editing rights are decided from the handshake token, not this
                // payload, so the join waits for a connection bound to this user.
                { forUserId: authUserId }
            );
        };

        join();

        // Presence in Redis expires on its own so a crashed server instance does
        // not leave ghosts in the room forever; this is what keeps it alive. A
        // STALE ack means our record expired anyway (a long sleep, an eviction),
        // so rejoin rather than keep beating at a record nobody can see.
        const heartbeat = setInterval(() => {
            if (!socket.connected) return;
            socket.emit('scenarioHeartbeat', { scenarioId }, (response) => {
                if (response?.status === 'STALE') join();
            });
        }, HEARTBEAT_MS);

        const handleRemoteSelection = ({ userId, oldSelection, newSelection }) => {
            if (userId === authUserId) return;
            setAwareness((state) => {
                let updated = state;
                if (oldSelection?.type) updated = moveUser(updated, oldSelection.type, userId, null);
                if (newSelection?.type && newSelection?.id) {
                    updated = moveUser(updated, newSelection.type, userId, newSelection.id);
                }
                return updated;
            });
        };

        /**
         * The roster changed. Three things happen here, and the order matters
         * less than the fact that all three are driven off the roster itself
         * rather than off the event type:
         *
         *   - anyone who was in the previous roster and is not in this one has
         *     their awareness purged. This used to key on `type === 'leave'`,
         *     which only fires for a clean departure — a browser that was killed
         *     outright leaves a ghost holding an advisory lock on whatever it had
         *     selected, and the server's `prune` sweep (see socket.js) is the only
         *     event that ever mentions it. Diffing catches both, and a missed
         *     `leave` frame besides.
         *   - the roster is stored.
         *   - a newcomer gets our picture of the room — but only on a real join.
         *     Replying to a prune would have every editor broadcast a full sync at
         *     whoever happened to be pruned.
         */
        const handleActiveCollaborators = ({ userId, type, collaborators }) => {
            const roster = collaborators || [];
            const nextIds = new Set(roster.map((entry) => entry?.userId).filter(Boolean));
            const departed = prevRosterIdsRef.current.filter((id) => !nextIds.has(id));
            prevRosterIdsRef.current = [...nextIds];

            setActiveCollaboratorsList(roster);

            // `userId` for an explicit leave: the server removes the record before
            // it lists, so the departing user is already absent from `collaborators`
            // and the diff would find nothing if they had somehow never been seen.
            const gone = type === 'leave' && userId ? [...new Set([...departed, userId])] : departed;
            if (gone.length > 0) {
                setAwareness((state) => gone.reduce((acc, id) => removeUserEverywhere(acc, id), state));
            }

            if (type !== 'join' || !userId || userId === authUserId) return;

            // Somebody just arrived with an empty picture of the room. Send them
            // ours; the server relays it to everyone and they filter on `toUser`.
            socket.emit('currentSelectedNodes', {
                userId: authUserId,
                toUser: userId,
                scenarioId,
                registries: awarenessRef.current.registries,
                queues: awarenessRef.current.queues,
            });
        };

        /**
         * Somebody's answer to our join. First one wins.
         *
         * Every editor already in the room replies, and this used to take the last
         * reply to arrive — so with three people in a scenario, the newcomer's
         * picture of who-holds-what was decided by network ordering, and each
         * successive reply overwrote a state the earlier ones had already been
         * merged into. They are all describing the same room; one answer is enough,
         * and the first one is the one that arrived while the room was least likely
         * to have moved on.
         */
        const handleSync = ({ toUser, userId, registries, queues }) => {
            if (toUser !== authUserId || userId === authUserId || !registries) return;
            if (hasSyncedRef.current) return;
            hasSyncedRef.current = true;
            setAwareness({
                registries: { ...emptyRegistries(), ...registries },
                queues: { ...emptyQueues(), ...queues },
            });
        };

        socket.on('selectionChangedd', handleRemoteSelection);
        socket.on('activeCollaborators', handleActiveCollaborators);
        socket.on('syncSelectedItems', handleSync);

        return () => {
            clearInterval(heartbeat);
            socket.emit('leaveSharedScenario', { scenarioId });
            removeJoinEvent(joinEvent);
            setActiveCollaboratorsList([]);
            prevRosterIdsRef.current = [];
            hasSyncedRef.current = false;
            socket.off('selectionChangedd', handleRemoteSelection);
            socket.off('activeCollaborators', handleActiveCollaborators);
            socket.off('syncSelectedItems', handleSync);
        };
    }, [authUserId, scenarioId, user?.username, user?.email]);

    const value = useMemo(
        () => ({
            // selection, as ids
            selection,
            selectedNodeId: selection.type === 'situation' ? selection.id : null,
            selectedEdgeId: selection.type === 'choice' ? selection.id : null,
            selectedGroupId: selection.type === 'group' ? selection.id : null,
            selectedKeyId: selection.type === 'key' ? selection.id : null,
            selectedNodeIds,
            selectItem,
            clearSelection,
            selectNodes,
            dropSelectionsOf,

            // panels, modals, drag
            activeEditTab,
            setActiveEditTab,
            highlightedKeyId,
            setHighlightedKeyId,
            showTocEditor,
            setShowTocEditor,
            expandedEditorNodeId,
            setExpandedEditorNodeId,
            isDragging,
            setIsDragging,
            onDragStart,
            onDragEnd,
            reactFlowWrapperRef,
            edgeConnectingNodeId,
            setEdgeConnectingNodeId,

            // awareness
            selectedNodeIdsUser: awareness.registries.situation,
            selectedEdgeIdsUser: awareness.registries.choice,
            selectedGroupUsers: awareness.registries.group,
            selectedKeyUser: awareness.registries.key,
            selectedNodeQueue: awareness.queues.situation,
            selectedChoiceQueue: awareness.queues.choice,
            selectedGroupQueue: awareness.queues.group,
            selectedKeyQueue: awareness.queues.key,
            activeCollaboratorsList,
            otherCollaborators,
            hasCollaborators,
            permissionLevel,
            setPermissionLevel,
        }),
        [
            selection,
            selectedNodeIds,
            selectItem,
            clearSelection,
            selectNodes,
            dropSelectionsOf,
            activeEditTab,
            highlightedKeyId,
            showTocEditor,
            expandedEditorNodeId,
            isDragging,
            onDragStart,
            onDragEnd,
            edgeConnectingNodeId,
            awareness,
            activeCollaboratorsList,
            otherCollaborators,
            hasCollaborators,
            permissionLevel,
        ]
    );

    return <EditorUIContext.Provider value={value}>{children}</EditorUIContext.Provider>;
};

export const useEditorUI = () => {
    const context = useContext(EditorUIContext);
    if (!context) throw new Error('useEditorUI must be used inside an EditorUIProvider.');
    return context;
};

export default EditorUIContext;
export { moveUser, removeUserEverywhere, removeItemEverywhere };
